import {useState} from 'react'
import {Dropdown, Tag} from '@/components/atoms'
import {SELECTION_TYPE} from '@/utils/constants/commonConstants'
import './_dropdownWithTags.scss'

/**
 * DropdownWithTags - A component that renders a dropdown with tags that display the selected options.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} [props.isJsonWrap=false] - Determines if the dropdown should use JSON wrapping.
 * @param {Array<{id: string, displayText: string, isBadge: boolean}>} [props.childList=[]] - List of child elements for the dropdown.
 * @param {string} [props.label=''] - The label for the dropdown.
 * @param {boolean} [props.required=false] - Indicates if the dropdown is required.
 * @param {string} [props.placeholder='Select options'] - The placeholder text for the dropdown.
 * @param {('single'|'multiple')} [props.selection='multiple'] - The selection mode for the dropdown.
 * @param {string} [props.helperText=''] - The helper text for the dropdown.
 * @param {string} [props.errorMessage=''] - The error message for the dropdown.
 * @param {boolean} [props.isError=false] - Indicates if there is an error.
 * @param {Function} [props.onChange] - Callback function to handle dropdown changes and get the selected options from a parent component.
 * @param {Function} [props.onTagClose] - Callback function to handle tag close events.
 * @example
 * // Example usage in a parent component:
 * const ParentComponent = () => {
 *   const [selectedOptions, setSelectedOptions] = useState([]);
 *
 *   const handleDropdownChange = (value) => {
 *     setSelectedOptions(value);
 *     console.log('Selected options:', value);
 *   };
 *
 *   return (
 *     <DropdownWithTags
 *       childList={[
 *         { id: '1', displayText: 'Option 1' },
 *         { id: '2', displayText: 'Option 2' }
 *       ]}
 *       onChange={handleDropdownChange}
 *     />
 *   );
 * };
 * @returns {JSX.Element} The rendered Dropdown component.
 */
export default function DropdownWithTags({
  isJsonWrap = false,
  label = '',
  childList = [],
  required = false,
  placeholder = 'Select options',
  selection = SELECTION_TYPE.MULTIPLE,
  helperText = '',
  errorMessage = '',
  isError = false,
  onChange = (value) => {console.log('DropdownWithTags Selected options:', value)},
  onTagClose = (id) => {console.log('Tag closed:', id)},
  selectedOptions = []
}) {
  const [selectedOptionsCopy, setSelectedOptionsCopy] = useState(selectedOptions)

  // Handle select dropdown
  const handleSelect = value => {
    setSelectedOptionsCopy(value)
    // Call the onChange callback with the new value
    if (onChange) {
      onChange(value)
    }
  }

  // Handle remove tag
  const handleRemove = id => {
    const updatedOptions = selectedOptionsCopy.filter(option => option.id !== id)
    setSelectedOptionsCopy(updatedOptions)
    // Call the onChange callback with the updated value
    if (onChange) {
      onChange(updatedOptions)
    }
    // Call the onTagClose callback
    if (onTagClose) {
      onTagClose(id)
    }
  }

  // Format label with required indicator if needed
  // Format label with required indicator if needed
  let formattedLabel = '';
  if (label) {
    formattedLabel = required ? `${label}*` : label;
  }

  return (
    <div className="flex flex-row flex-wrap gap-2 dropdown-with-tags">
      <Dropdown
        isJsonWrap={isJsonWrap}
        label={formattedLabel}
        childList={childList}
        required={required}
        placeholder={placeholder}
        selection={selection}
        value={selectedOptionsCopy}
        updateValue={handleSelect}
        isError={isError}
        errorMessage={errorMessage}
        helperText={helperText}
      />

      <div className="flex flex-row flex-wrap gap-2 items-center justify-center dropdown-with-tags__tags_container">
        {selectedOptionsCopy &&
          selectedOptionsCopy.map(option => (
            <Tag
              key={option.id}
              id={option.id}
              text={option.displayText}
              isRemovable={true}
              onRemove={() => handleRemove(option.id)}
            />
        ))}
      </div>
    </div>
  )
}
